import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { me, signOut, API_BASE, listDevices } from "@/lib/api";
import { User_Plan } from "@/gen/tabgraph/v1/user_pb";
import type { Device } from "@/gen/tabgraph/v1/device_pb";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/hooks/useToast";
import { formatDistanceToNow } from "@/lib/dateUtils";

/**
 * Human-readable device label, e.g. "Chrome on macOS". Devices registered
 * before browser/OS collection existed (or that haven't re-registered since)
 * may have one or both fields blank — degrade gracefully rather than show a
 * broken string.
 */
function deviceLabel(device: Device): string {
  if (device.browser && device.os) return `${device.browser} on ${device.os}`;
  if (device.browser) return device.browser;
  if (device.os) return `Unknown browser on ${device.os}`;
  return "Unknown device";
}

export function SettingsPage() {
  const { isSignedIn, signOut: storeSignOut } = useAuthStore();
  const navigate = useNavigate();
  const [deleteDialogStep, setDeleteDialogStep] = useState<
    "closed" | "first" | "second"
  >("closed");

  useEffect(() => {
    if (!isSignedIn) void navigate("/", { replace: true });
  }, [isSignedIn, navigate]);

  const { data: user, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    enabled: isSignedIn,
  });

  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ["devices"],
    queryFn: listDevices,
    enabled: isSignedIn,
  });

  if (!isSignedIn) return null;

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold">Settings</h1>

      {/* Account */}
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-32" />
            </>
          ) : user ? (
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Email</p>
                <p className="text-sm font-medium">{user.email}</p>
              </div>
              {user.displayName && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Display name
                  </p>
                  <p className="text-sm font-medium">{user.displayName}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Plan</p>
                <div className="flex items-center gap-2">
                  <Badge variant={user.plan === User_Plan.PRO ? "default" : "secondary"}>
                    {user.plan === User_Plan.PRO ? "Pro" : "Free"}
                  </Badge>
                  {user.plan === User_Plan.PRO && (
                    <span className="text-xs text-muted-foreground">
                      {user.subscriptionActive
                        ? "Subscription active"
                        : "Subscription inactive"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Could not load account details.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Devices */}
      <Card>
        <CardHeader>
          <CardTitle>Devices</CardTitle>
          <CardDescription>
            Browser installs connected to your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {devicesLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : devices && devices.length > 0 ? (
            <ul className="space-y-2">
              {devices.map((device) => (
                <li
                  key={device.id}
                  className="flex items-center justify-between gap-4 rounded-md border px-3 py-2"
                >
                  <span className="text-sm font-medium">
                    {deviceLabel(device)}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {device.lastSeenAt
                      ? `Last seen ${formatDistanceToNow(
                          new Date(Number(device.lastSeenAt.seconds) * 1000),
                        )}`
                      : "Never seen"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              No browsers connected yet.{" "}
              <a
                href="https://tabgraph.stratusstudios.com/#install"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-4"
              >
                Install the extension
              </a>{" "}
              to connect this browser.
            </p>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Danger zone */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Danger zone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Delete all my data</p>
              <p className="text-xs text-muted-foreground">
                Permanently remove your account, all sessions, and graph data.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteDialogStep("first")}
            >
              Delete account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* First confirm */}
      <Dialog
        open={deleteDialogStep === "first"}
        onOpenChange={(open) => {
          if (!open) setDeleteDialogStep("closed");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This will permanently delete your account and all associated data
              including every session and graph. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogStep("closed")}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogStep("second")}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Second confirm */}
      <Dialog
        open={deleteDialogStep === "second"}
        onOpenChange={(open) => {
          if (!open) setDeleteDialogStep("closed");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Final confirmation</DialogTitle>
            <DialogDescription>
              Click &ldquo;Delete everything&rdquo; to permanently erase your
              account. There is no recovery.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogStep("closed")}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                // TODO: wire to backend DELETE /v1/me once available
                await signOut(API_BASE);
                storeSignOut();
                void navigate("/");
                toast({ title: "Account deleted" });
              }}
            >
              Delete everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
